import { useMemo, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { z } from 'zod';
import { PROPOSAL_STATUSES } from '@mkcore/proposals/schema';
import { Button, EmptyState, ErrorState, Field, Input, NativeSelect, PageHeader, Skeleton } from '@mkcore/ui';
import { useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { formatDate } from '@/lib/format';
import { DataTable, type ColumnDef } from '@/components/data-table';
import { NewProposalDialog, ProposalStatusBadge, eur, proposalsQuery, type ProposalRow as Row } from '@/features/proposals/proposals';

const search = z.object({ q: z.string().optional(), status: z.enum(PROPOSAL_STATUSES).optional() });
type Search = z.infer<typeof search>;

export const Route = createFileRoute('/_app/proposals/')({ validateSearch: search, component: Proposals });


/** Proposals (CO-0595): newest first, with number, client, status and the total of the latest version. */
function Proposals() {
  const { t, i18n } = useTranslation();
  const s = Route.useSearch();
  const message = useErrorMessage();
  const navigate = useNavigate({ from: Route.fullPath });
  const set = (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const canCreate = useCan('proposals:create');
  const [creating, setCreating] = useState(false);
  const locale = i18n.language === 'en' ? 'en' : 'nl';
  const q = useInfiniteQuery(proposalsQuery({ q: s.q || undefined, status: s.status ? [s.status] : undefined }));
  const rows = useMemo(() => q.data?.pages.flatMap((p) => p.items) ?? [], [q.data]);
  const columns = useMemo<ColumnDef<Row>[]>(
    () => [
      {
        id: 'number',
        label: t('proposals.fields.number'),
        value: (p) => p.number,
        cell: (p) => (
          <Link to="/proposals/$id" params={{ id: p.id }} className="font-bold hover:underline">
            {p.number}
          </Link>
        ),
      },
      { id: 'title', label: t('proposals.fields.title'), value: (p) => p.title, cell: (p) => p.title },
      { id: 'client', label: t('proposals.fields.organisation'), groupable: true, value: (p) => p.organisationName, cell: (p) => p.organisationName },
      {
        id: 'status',
        label: t('proposals.fields.status'),
        type: 'enum',
        options: PROPOSAL_STATUSES.map((x) => ({ value: x, label: t(`proposals.status.${x}`) })),
        groupable: true,
        value: (p) => p.status,
        cell: (p) => <ProposalStatusBadge status={p.status} />,
      },
      { id: 'total', label: t('proposals.total'), type: 'number', value: (p) => p.totalCents, cell: (p) => eur(p.totalCents, p.currency, locale), className: 'text-right tabular' },
      { id: 'validUntil', label: t('proposals.fields.validUntil'), type: 'date', value: (p) => p.validUntil, cell: (p) => (p.validUntil ? formatDate(p.validUntil) : '–'), className: 'hidden md:table-cell' },
      { id: 'owner', label: t('proposals.fields.owner'), groupable: true, hidden: true, value: (p) => p.ownerName, cell: (p) => p.ownerName ?? '–' },
    ],
    [t, locale],
  );

  return (
    <>
      <PageHeader
        title={t('proposals.title')}
        description={t('proposals.description')}
        actions={
          canCreate && (
            <Button onClick={() => setCreating(true)}>
              <Plus /> {t('proposals.new')}
            </Button>
          )
        }
      />
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label={t('common.search')} className="w-64">
          {(p) => <Input {...p} value={s.q ?? ''} onChange={(e) => set({ q: e.target.value || undefined })} />}
        </Field>
        <Field label={t('proposals.fields.status')} className="w-44">
          {(p) => (
            <NativeSelect {...p} value={s.status ?? ''} onChange={(e) => set({ status: (e.target.value || undefined) as never })}>
              <option value="">{t('common.all')}</option>
              {PROPOSAL_STATUSES.map((x) => (
                <option key={x} value={x}>
                  {t(`proposals.status.${x}`)}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
      </div>
      {q.isError ? (
        <ErrorState title={t('errors.generic')} description={message(q.error)} />
      ) : q.isLoading ? (
        <Skeleton className="h-64" />
      ) : !rows.length ? (
        <EmptyState title={t('proposals.empty')} description={canCreate ? t('proposals.emptyHint') : undefined} />
      ) : (
        <DataTable listKey="proposal.proposal.list" columns={columns} rows={rows} rowKey={(p) => p.id} onOpen={(p) => void navigate({ to: '/proposals/$id', params: { id: p.id } })} partial={q.hasNextPage} onNeedAll={() => q.hasNextPage && !q.isFetchingNextPage && void q.fetchNextPage()} />
      )}
      {creating && <NewProposalDialog onClose={() => setCreating(false)} />}
    </>
  );
}
